import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { QrCode, ArrowRight, MapPin, Heart, Star, Sparkles } from 'lucide-react';

export const HeroClassical = () => {
  const navigate = useNavigate();
  
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };
  return (
    <div className="relative overflow-hidden" style={{
      background: 'linear-gradient(135deg, #d4b896 0%, #c9a86a 25%, #b8956a 50%, #a67c52 75%, #8b6f47 100%)',
      position: 'relative'
    }}>
      {/* Vintage Globe/Map Photo Overlay */}
      <div className="absolute inset-0 opacity-15" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1576379392292-1c898ea31764?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHx2aW50YWdlJTIwZ2xvYmV8ZW58MHx8fHwxNzYzMjUyNjI2fDA&ixlib=rb-4.1.0&q=85")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        mixBlendMode: 'multiply',
        filter: 'sepia(0.3) contrast(0.9)'
      }}></div>
      
      {/* Vintage Map Texture Overlay */}
      <div className="absolute inset-0 opacity-20" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v100H0z' fill='%23ffffff'/%3E%3Cpath d='M20 20l60 60M80 20L20 80' stroke='%23000' stroke-width='0.5' opacity='0.1'/%3E%3C/svg%3E")`,
        backgroundSize: '50px 50px'
      }}></div>
      
      {/* Aged Paper Texture */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'radial-gradient(circle at 20% 50%, transparent 0%, rgba(101, 67, 33, 0.3) 100%)',
        mixBlendMode: 'multiply'
      }}></div>

      {/* Vintage Nautical Elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Compass Rose - Top Left */}
        <div className="absolute top-10 left-10 opacity-30 animate-spin" style={{animationDuration: '120s'}}>
          <svg width="120" height="120" viewBox="0 0 100 100" className="drop-shadow-lg">
            <circle cx="50" cy="50" r="45" fill="none" stroke="#654321" strokeWidth="2"/>
            <circle cx="50" cy="50" r="35" fill="none" stroke="#654321" strokeWidth="1"/>
            <path d="M50,5 L55,45 L50,50 L45,45 Z" fill="#8B4513"/>
            <path d="M95,50 L55,55 L50,50 L55,45 Z" fill="#A0522D"/>
            <path d="M50,95 L45,55 L50,50 L55,55 Z" fill="#8B4513"/>
            <path d="M5,50 L45,45 L50,50 L45,55 Z" fill="#A0522D"/>
            <text x="50" y="15" textAnchor="middle" fill="#654321" fontSize="12" fontWeight="bold">N</text>
            <text x="85" y="55" textAnchor="middle" fill="#654321" fontSize="10">E</text>
            <text x="50" y="92" textAnchor="middle" fill="#654321" fontSize="10">S</text>
            <text x="15" y="55" textAnchor="middle" fill="#654321" fontSize="10">W</text>
          </svg>
        </div>

        {/* Ship Silhouette - Top Right */}
        <div className="absolute top-16 right-12 opacity-25 animate-float">
          <svg width="140" height="140" viewBox="0 0 200 200" className="drop-shadow-xl">
            <g transform="translate(100, 100)">
              {/* Ship Hull */}
              <path d="M-40,40 L-50,60 L50,60 L40,40 Z" fill="#654321" stroke="#3d2817" strokeWidth="2"/>
              {/* Masts */}
              <line x1="-20" y1="40" x2="-20" y2="-40" stroke="#3d2817" strokeWidth="3"/>
              <line x1="10" y1="40" x2="10" y2="-50" stroke="#3d2817" strokeWidth="3"/>
              {/* Sails */}
              <path d="M-20,-40 L-55,-20 L-20,0 Z" fill="#d4b896" stroke="#654321" strokeWidth="1.5" opacity="0.9"/>
              <path d="M10,-50 L-25,-30 L10,-10 Z" fill="#d4b896" stroke="#654321" strokeWidth="1.5" opacity="0.9"/>
              <path d="M10,-10 L-15,5 L10,20 Z" fill="#c9a86a" stroke="#654321" strokeWidth="1.5" opacity="0.9"/>
              {/* Flag */}
              <path d="M10,-50 L10,-55 L25,-52 Z" fill="#8B4513"/>
            </g>
          </svg>
        </div>

        {/* Vintage Globe - Bottom Left */}
        <div className="absolute bottom-10 left-16 opacity-20 animate-pulse" style={{animationDuration: '4s'}}>
          <svg width="100" height="100" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="40" fill="none" stroke="#654321" strokeWidth="2"/>
            <ellipse cx="50" cy="50" rx="40" ry="15" fill="none" stroke="#654321" strokeWidth="1.5"/>
            <ellipse cx="50" cy="50" rx="15" ry="40" fill="none" stroke="#654321" strokeWidth="1.5"/>
            <path d="M10,50 Q50,30 90,50" fill="none" stroke="#654321" strokeWidth="1"/>
            <path d="M10,50 Q50,70 90,50" fill="none" stroke="#654321" strokeWidth="1"/>
          </svg>
        </div>

        {/* Sextant - Bottom Right */}
        <div className="absolute bottom-16 right-20 opacity-25 animate-float" style={{animationDelay: '2s'}}>
          <svg width="90" height="90" viewBox="0 0 100 100">
            <path d="M20,80 L80,80 L50,30 Z" fill="none" stroke="#654321" strokeWidth="3"/>
            <circle cx="50" cy="30" r="8" fill="#8B4513" stroke="#654321" strokeWidth="2"/>
            <line x1="50" y1="30" x2="75" y2="15" stroke="#654321" strokeWidth="2"/>
            <circle cx="75" cy="15" r="4" fill="#DAA520"/>
            <path d="M30,75 Q50,65 70,75" fill="none" stroke="#654321" strokeWidth="2"/>
          </svg>
        </div>

        {/* Vintage Map Grid Lines */}
        <div className="absolute inset-0 opacity-10">
          <svg width="100%" height="100%" style={{position: 'absolute'}}>
            <defs>
              <pattern id="grid" width="100" height="100" patternUnits="userSpaceOnUse">
                <path d="M 100 0 L 0 0 0 100" fill="none" stroke="#654321" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        {/* Decorative Corner Elements */}
        <div className="absolute top-0 left-0 w-24 h-24 opacity-20">
          <svg viewBox="0 0 100 100">
            <path d="M0,0 L30,0 Q35,5 30,10 L0,40 Z" fill="#654321"/>
            <path d="M0,0 L0,30 Q5,35 10,30 L40,0 Z" fill="#654321"/>
          </svg>
        </div>
        <div className="absolute top-0 right-0 w-24 h-24 opacity-20 transform scale-x-[-1]">
          <svg viewBox="0 0 100 100">
            <path d="M0,0 L30,0 Q35,5 30,10 L0,40 Z" fill="#654321"/>
            <path d="M0,0 L0,30 Q5,35 10,30 L40,0 Z" fill="#654321"/>
          </svg>
        </div>
      </div>

      <div className="relative z-10 container mx-auto px-4 pt-16 md:pt-20 pb-8 md:pb-12">
        <div className="max-w-4xl mx-auto text-center">
          {/* Hero Content */}
          <div className="mb-6 md:mb-8">
            <div className="inline-flex items-center gap-2 px-3 md:px-4 py-2 rounded-full font-medium mb-4 md:mb-6 shadow-lg text-sm md:text-base" style={{
              background: 'linear-gradient(135deg, rgba(139, 69, 19, 0.9), rgba(160, 82, 45, 0.9))',
              color: '#f4e4c1',
              border: '2px solid #8B4513',
              fontFamily: 'Georgia, serif'
            }}>
              <Sparkles className="w-4 h-4" />
              <span>Chart Your Journey</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-7xl font-bold mb-4 md:mb-6 leading-tight" style={{
              color: '#2c1810',
              textShadow: '2px 2px 4px rgba(0,0,0,0.2)',
              fontFamily: 'Georgia, serif'
            }}>
              Every Person, Item and Location Has a
              <span className="block" style={{
                background: 'linear-gradient(135deg, #654321, #8B4513, #A0522D)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Story to Tell
              </span>
            </h1>
            
            {/* Three Column Layout: Feature Cards + Text Box */}
            <div className="hidden md:grid md:grid-cols-12 gap-6 items-center max-w-7xl mx-auto mb-6">
              {/* Left Card - Traveling Items */}
              <div className="col-span-3">
                <div className="rounded-xl p-4 text-left hover:shadow-xl transition-all duration-300 transform hover:scale-105 h-full" style={{
                  background: 'linear-gradient(135deg, rgba(244, 228, 193, 0.95), rgba(218, 196, 153, 0.95))',
                  border: '3px solid #8B4513',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3)'
                }}>
                  <div className="flex flex-col gap-3">
                    <div className="rounded-full p-3 w-fit" style={{
                      background: 'linear-gradient(135deg, #654321, #8B4513)',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                    }}>
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold mb-2" style={{color: '#2c1810', fontFamily: 'Georgia, serif'}}>Traveling Items</h3>
                      <p className="text-xs leading-relaxed" style={{color: '#3d2817', fontFamily: 'Georgia, serif'}}>
                        Attach a QR code to your item and watch it journey across the world. Track its path and collect stories from every finder.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Center - Text Box (narrower and taller) */}
              <div className="col-span-6 flex items-center justify-center">
                <div className="px-6 py-8 rounded-2xl shadow-2xl transform hover:scale-105 transition-all duration-300 w-full" style={{
                  background: 'linear-gradient(135deg, rgba(255, 248, 220, 0.98), rgba(245, 235, 210, 0.98))',
                  border: '4px solid #8B4513',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.3), inset 0 2px 4px rgba(255,255,255,0.5)',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h60v60H0z' fill='%23f5ebd2'/%3E%3Cpath d='M30 30m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0' fill='%23d4a574' opacity='0.1'/%3E%3C/svg%3E")`
                }}>
                  <p className="text-lg leading-relaxed font-medium text-center" style={{color: '#2c1810', fontFamily: 'Georgia, serif'}}>
                    Now you can <span className="font-bold" style={{color: '#654321'}}>start</span> a <span className="font-bold" style={{color: '#8B4513'}}>Random Act of Kindness</span> and track adventures as items travel the world, or sign guestbooks at your favorite places.
                  </p>
                  <p className="mt-4 font-semibold text-center text-base" style={{color: '#654321', fontFamily: 'Georgia, serif', fontStyle: 'italic'}}>
                    Every journey creates memories, every location collects stories.
                  </p>
                </div>
              </div>
              
              {/* Right Card - Location Guest Book */}
              <div className="col-span-3">
                <div className="rounded-xl p-4 text-left hover:shadow-xl transition-all duration-300 transform hover:scale-105 h-full" style={{
                  background: 'linear-gradient(135deg, rgba(244, 228, 193, 0.95), rgba(218, 196, 153, 0.95))',
                  border: '3px solid #8B4513',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3)'
                }}>
                  <div className="flex flex-col gap-3">
                    <div className="rounded-full p-3 w-fit" style={{
                      background: 'linear-gradient(135deg, #654321, #8B4513)',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                    }}>
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold mb-2" style={{color: '#2c1810', fontFamily: 'Georgia, serif'}}>Location Guest Book</h3>
                      <p className="text-xs leading-relaxed" style={{color: '#3d2817', fontFamily: 'Georgia, serif'}}>
                        Turn any place into a digital guestbook. Visitors scan your QR code to leave memories, photos, and stories at that spot.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Mobile Layout - Stacked */}
            <div className="md:hidden mb-6">
              <p className="text-base mb-4 max-w-3xl mx-auto leading-relaxed font-medium">
                <span className="px-4 py-6 rounded-2xl shadow-2xl inline-block" style={{
                  background: 'linear-gradient(135deg, rgba(255, 248, 220, 0.98), rgba(245, 235, 210, 0.98))',
                  border: '3px solid #8B4513',
                  color: '#2c1810',
                  fontFamily: 'Georgia, serif'
                }}>
                  Now you can <span className="font-bold" style={{color: '#654321'}}>start</span> a <span className="font-bold" style={{color: '#8B4513'}}>Random Act of Kindness</span> and track adventures as items travel the world, or sign guestbooks at your favorite places. <span className="block mt-2 font-semibold" style={{color: '#654321', fontStyle: 'italic'}}>Every journey creates memories, every location collects stories.</span>
                </span>
              </p>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center mb-8 md:mb-12">
            <Button 
              size="lg" 
              className="px-6 md:px-8 py-3 md:py-4 text-base md:text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, #654321, #8B4513, #A0522D)',
                color: '#f4e4c1',
                border: '3px solid #3d2817',
                fontFamily: 'Georgia, serif',
                fontWeight: 'bold',
                textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
              }}
              onClick={() => navigate('/create-item')}
            >
              <Heart className="w-4 md:w-5 h-4 md:h-5 mr-2" />
              Start Your Journey
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              className="px-6 md:px-8 py-3 md:py-4 text-base md:text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              style={{
                border: '3px solid #654321',
                color: '#2c1810',
                background: 'rgba(244, 228, 193, 0.8)',
                fontFamily: 'Georgia, serif',
                fontWeight: 'bold'
              }}
              onClick={() => scrollToSection('map')}
            >
              <MapPin className="w-4 md:w-5 h-4 md:h-5 mr-2" />
              Explore Stories
            </Button>
          </div>

          {/* Journey Wave Animation - Reduced on Mobile */}
          <div className="mb-4 md:mb-8 relative hidden md:block">
            <svg className="w-full h-24" viewBox="0 0 800 96" fill="none">
              <path
                d="M0 48 Q200 20 400 48 T800 48"
                stroke="url(#journeyGradient)"
                strokeWidth="3"
                strokeLinecap="round"
                className="animate-pulse"
              />
              <defs>
                <linearGradient id="journeyGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#f472b6" stopOpacity="0.6" />
                  <stop offset="50%" stopColor="#a78bfa" stopOpacity="0.8" />
                  <stop offset="100%" stopColor="#60a5fa" stopOpacity="0.6" />
                </linearGradient>
              </defs>
              
              {/* Traveling dots along the journey path */}
              <circle r="4" fill="#f472b6" className="opacity-80">
                <animateMotion dur="6s" repeatCount="indefinite">
                  <mpath href="#journeyPath" />
                </animateMotion>
              </circle>
              <circle r="3" fill="#a78bfa" className="opacity-90">
                <animateMotion dur="8s" repeatCount="indefinite" begin="1s">
                  <mpath href="#journeyPath" />
                </animateMotion>
              </circle>
              <circle r="2" fill="#60a5fa" className="opacity-70">
                <animateMotion dur="10s" repeatCount="indefinite" begin="3s">
                  <mpath href="#journeyPath" />
                </animateMotion>
              </circle>
              <path id="journeyPath" d="M0 48 Q200 20 400 48 T800 48" fill="none" />
            </svg>
            
            {/* Journey milestone indicators */}
            <div className="absolute top-1/2 left-0 transform -translate-y-1/2">
              <div className="w-3 h-3 bg-pink-500 rounded-full shadow-lg animate-pulse"></div>
            </div>
            <div className="absolute top-1/2 left-1/4 transform -translate-y-1/2">
              <div className="w-2 h-2 bg-purple-500 rounded-full shadow-lg animate-pulse" style={{animationDelay: '1s'}}></div>
            </div>
            <div className="absolute top-1/2 left-1/2 transform -translate-y-1/2">
              <div className="w-3 h-3 bg-blue-500 rounded-full shadow-lg animate-pulse" style={{animationDelay: '2s'}}></div>
            </div>
            <div className="absolute top-1/2 left-3/4 transform -translate-y-1/2">
              <div className="w-2 h-2 bg-purple-500 rounded-full shadow-lg animate-pulse" style={{animationDelay: '3s'}}></div>
            </div>
            <div className="absolute top-1/2 right-0 transform -translate-y-1/2">
              <div className="w-3 h-3 bg-pink-500 rounded-full shadow-lg animate-pulse" style={{animationDelay: '4s'}}></div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating QR Code Animation */}
      <div className="absolute bottom-10 right-10 animate-bounce">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-xl">
          <QrCode className="w-12 h-12 text-purple-600" />
        </div>
      </div>
    </div>
  );
};